/**
 * @fileoverview Bulk Approve dialog for Sales Pipeline's Pending Approval tab.
 * @description Approves the ticked review requests in one call (POST /approvals/bulk-approve, 1 to
 * 50 requests, each at the version the list showed), with one optional review note for all. The
 * backend decides each request on its own, so a request that was decided meanwhile, changed, or is
 * outside the reviewer's authority is skipped with its reason rather than failing the batch: when
 * any were skipped the dialog lists them before closing. Close is held while the request is in
 * flight, and the outcome is handled by the mutation itself (so it survives an unmount). The parent
 * mounts it only while it's open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { BULK_APPROVE_MAX_REQUESTS } from '@/features/sales/constants/bulk-approve.constants'
import { useBulkApproveApprovals } from '@/features/sales/hooks/useBulkApproveApprovals'
import {
  bulkApproveFormSchema,
  type BulkApproveFormValues,
} from '@/features/sales/schemas/bulk-approve-form.schema'
import { REVIEW_NOTE_MAX_LENGTH } from '@/features/sales/schemas/reject-approval-form.schema'
import Button from '@/shared/components/atoms/Button'
import Label from '@/shared/components/atoms/Label'
import Modal from '@/shared/components/molecules/Modal'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import DialogActions from './DialogActions'
import SalesRequestError from './SalesRequestError'

import type {
  ApprovalListItem,
  BulkApproveItem,
  BulkApproveResult,
} from '@/features/sales/types/approval.types'
import type { FormEvent } from 'react'

interface BulkApproveDialogProps {
  requests: ApprovalListItem[]
  onClose: () => void
  onDone: (result: BulkApproveResult) => void
}

/**
 * @component
 * @param {BulkApproveDialogProps} props - The ticked requests and the handlers.
 * @param {ApprovalListItem[]} props.requests - The pending requests to approve.
 * @param {() => void} props.onClose - Called when the dialog is dismissed before approving.
 * @param {(result: BulkApproveResult) => void} props.onDone - Called with the outcome once the
 * person has seen it (clears the ticks).
 */
function BulkApproveDialog({ requests, onClose, onDone }: BulkApproveDialogProps) {
  const { t } = useTranslation()
  const [result, setResult] = useState<BulkApproveResult | null>(null)
  const bulk = useBulkApproveApprovals({ onSuccess: showOutcome })
  const close = useGuardedClose(onClose, bulk.isPending)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BulkApproveFormValues>({
    resolver: zodResolver(bulkApproveFormSchema),
    defaultValues: { reviewNote: '' },
  })
  const tooMany = requests.length > BULK_APPROVE_MAX_REQUESTS

  /**
   * @description Reports the outcome at once, or lists what was skipped first.
   * @param {BulkApproveResult} outcome - What the batch answered.
   */
  function showOutcome(outcome: BulkApproveResult) {
    if (outcome.skipped.length === 0) onDone(outcome)
    else setResult(outcome)
  }

  /**
   * @description Names a listed request as the item the batch sends for it.
   * @param {ApprovalListItem} request - A ticked request.
   * @returns {BulkApproveItem} The request id with the version it was listed at.
   */
  const toItem = (request: ApprovalListItem): BulkApproveItem => ({
    approvalRequestId: request.approvalRequestId,
    approvalVersion: request.approvalVersion,
  })

  /**
   * @description Approves the batch; the mutation reports it (or lists what was skipped first).
   * @param {BulkApproveFormValues} values - The form's values.
   */
  const onValid = (values: BulkApproveFormValues) => {
    bulk.mutate({ items: requests.map(toItem), reviewNote: values.reviewNote || null })
  }

  /**
   * @description Validates and submits the form.
   * @param {FormEvent<HTMLFormElement>} event - The submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  /**
   * @description Names the bank a listed request belongs to, falling back to the request id.
   * @param {string} approvalRequestId - The request.
   * @returns {string} The bank name, or the request id when it is not in the list.
   */
  const bankOf = (approvalRequestId: string) =>
    requests.find((request) => request.approvalRequestId === approvalRequestId)?.bankName ??
    approvalRequestId

  /**
   * @description Hands the shown outcome back to the parent (clears the ticks).
   */
  const dismissResult = () => {
    if (result) onDone(result)
  }
  const noteError = errors.reviewNote?.message

  return (
    <Modal
      open
      onClose={result ? dismissResult : close}
      data-testid="bulk-approve-dialog"
      title={t('sales.pipeline.bulkApprove.title')}
      subtitle={t('sales.pipeline.bulkApprove.subtitle', { count: requests.length })}
    >
      {result ? (
        <div className="flex flex-col gap-4" data-testid="bulk-approve-result">
          <p className="text-[13.5px] text-ink" data-testid="bulk-approve-result-summary">
            {t('sales.pipeline.bulkApprove.result', {
              approved: result.approved.length,
              skipped: result.skipped.length,
            })}
          </p>
          <ul className="flex flex-col gap-1.5 text-[13px] text-danger">
            {result.skipped.map((item) => (
              <li key={item.approvalRequestId} data-testid="bulk-approve-skipped-item">
                {bankOf(item.approvalRequestId)}: {item.message}
              </li>
            ))}
          </ul>
          <div className="w-fit">
            <Button
              type="button"
              size="md"
              onClick={dismissResult}
              data-testid="bulk-approve-result-dismiss-button"
            >
              {t('sales.pipeline.flash.dismiss')}
            </Button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          noValidate
          className="flex flex-col gap-4"
          data-testid="bulk-approve-form"
        >
          <p className="text-[13.5px] text-ink">{t('sales.pipeline.bulkApprove.message')}</p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="bulkApproveNote">{t('sales.pipeline.bulkApprove.noteLabel')}</Label>
            <textarea
              id="bulkApproveNote"
              data-testid="bulk-approve-note-input"
              rows={3}
              maxLength={REVIEW_NOTE_MAX_LENGTH}
              className="w-full rounded-lg border border-input-border bg-white px-3 py-2 text-[13.5px] text-ink"
              {...register('reviewNote')}
            />
            {noteError && (
              <span
                className="text-[11.5px] font-medium text-danger"
                data-testid="bulk-approve-note-error"
              >
                {t(noteError)}
              </span>
            )}
          </div>
          {tooMany && (
            <p role="alert" className="text-[13px] text-danger" data-testid="bulk-approve-too-many">
              {t('sales.pipeline.bulkApprove.errors.tooMany')}
            </p>
          )}
          {bulk.isError && <SalesRequestError error={bulk.error} />}
          <DialogActions
            testIdPrefix="bulk-approve"
            cancelLabel={t('sales.pipeline.form.cancel')}
            confirmLabel={t('sales.pipeline.bulkApprove.submit', { count: requests.length })}
            onCancel={close}
            confirmType="submit"
            isLoading={bulk.isPending}
            disabled={tooMany}
          />
        </form>
      )}
    </Modal>
  )
}

export default BulkApproveDialog
